import React from 'react';
import { ArrowRight, Ban, Megaphone, Save, Upload } from 'lucide-react';
import { Badge } from '../Scheduler/utils';
import { buildGigWarnings } from '../Scheduler/schedulerView';
import { formatGigDisplayStatusLabel, getGigDisplayStatus, isGigCompleted } from './gigStatus';

const STATUS_LABELS = {
  draft: 'Draft',
  awaiting_submission: 'Awaiting Submission',
  submitted: 'Submitted',
  published: 'Published',
  completed: 'Completed',
  cancelled: 'Cancelled',
  replaced: 'Replaced',
};

const STATUS_BADGES = {
  draft: 'bg-slate-500/20 text-slate-200',
  awaiting_submission: 'bg-amber-500/20 text-amber-100',
  submitted: 'bg-sky-500/20 text-sky-100',
  published: 'bg-emerald-500/20 text-emerald-100',
  completed: 'bg-cyan-500/20 text-cyan-100',
  cancelled: 'bg-rose-500/20 text-rose-100',
  replaced: 'bg-violet-500/20 text-violet-100',
};

function formatLabel(value) {
  return STATUS_LABELS[String(value || '').toLowerCase()] || 'Draft';
}

function formatTimestamp(value) {
  if (!value) return 'Not yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not yet';
  return date.toLocaleString();
}

function isHistoricalPerformer(performer) {
  return ['cancelled', 'replaced'].includes(String(performer?.status || '').toLowerCase());
}

function formatPerformerWindow(performer) {
  const start = String(performer?.setStartTime || '').slice(0, 5);
  const end = String(performer?.setEndTime || '').slice(0, 5);
  if (start && end) return `${start} – ${end}`;
  if (start) return `Starts ${start}`;
  if (end) return `Ends ${end}`;
  return 'Set time not assigned';
}

export default function GigReviewPanel({
  gig,
  performers,
  artistsById,
  busy,
  onSave,
  onRequestChanges,
  onPublish,
  onCancel,
  onCreateCampaign,
}) {
  const status = getGigDisplayStatus(gig);
  const allPerformers = Array.isArray(performers) ? performers : [];
  const activePerformers = allPerformers.filter((performer) => !isHistoricalPerformer(performer));
  const historyPerformers = allPerformers.filter((performer) => isHistoricalPerformer(performer));
  const warnings = buildGigWarnings({ performers: allPerformers });
  const completedNeedsHighlights = isGigCompleted(gig) && !(Array.isArray(gig?.highlightPhotos) && gig.highlightPhotos.length);
  const displayWarnings = completedNeedsHighlights
    ? [
        ...warnings,
        'This gig is completed. Upload post-event photos in Photo Highlights for the public Previous Events page.',
      ]
    : warnings;
  const submittedCount = activePerformers.filter(
    (performer) => String(performer?.status || '').toLowerCase() === 'submitted'
  ).length;
  const awaitingCount = activePerformers.filter(
    (performer) => String(performer?.status || '').toLowerCase() === 'awaiting_submission'
  ).length;
  const canRequestChanges = gig?.id && status !== 'cancelled';
  const canPublish = gig?.id && status !== 'cancelled';
  const canCancel = gig?.id && status !== 'cancelled';
  const canCreateCampaign = gig?.id && ['published', 'completed'].includes(status);
  const artworkEntries = activePerformers.filter((performer) => performer?.artworkThumbnailUrl);

  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-white">Review & Actions</div>
          <div className="text-xs text-slate-400">Manager workflow for the selected gig lineup.</div>
        </div>
        <Badge className={STATUS_BADGES[status] || STATUS_BADGES.draft}>{formatGigDisplayStatusLabel(status)}</Badge>
      </div>

      <div className="mt-4 grid gap-2 text-sm">
        <div className="flex items-center justify-between gap-3 rounded-xl bg-black/20 px-3 py-2">
          <span className="text-slate-400">Active performers</span>
          <span className="text-white">{activePerformers.length}</span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl bg-black/20 px-3 py-2">
          <span className="text-slate-400">Submitted</span>
          <span className="text-white">{submittedCount}</span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl bg-black/20 px-3 py-2">
          <span className="text-slate-400">Awaiting materials</span>
          <span className="text-white">{awaitingCount}</span>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl bg-black/20 px-3 py-2">
          <span className="text-slate-400">Published</span>
          <span className="text-white">{formatTimestamp(gig?.publishedAt)}</span>
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-black/20 p-3">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Lineup Status</div>
        <div className="mt-3 grid gap-2">
          {allPerformers.length ? (
            allPerformers.map((performer, index) => {
              const performerStatus = String(performer?.status || 'awaiting_submission').toLowerCase();
              const linkedArtist = artistsById?.get?.(Number(performer?.artistId));
              return (
                <div key={performer?.id || `performer-${index}`} className="rounded-xl bg-white/5 px-3 py-2 ring-1 ring-white/10">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-white">{performer?.displayName || `Performer ${index + 1}`}</div>
                      <div className="mt-0.5 text-xs text-slate-400">
                        {linkedArtist?.name || formatPerformerWindow(performer)}
                      </div>
                    </div>
                    <Badge className={STATUS_BADGES[performerStatus] || STATUS_BADGES.awaiting_submission}>
                      {formatLabel(performerStatus)}
                    </Badge>
                  </div>
                  <div className="mt-2 grid gap-1 text-[11px] text-slate-400">
                    <div>Invite sent: {formatTimestamp(performer?.inviteSentAt)}</div>
                    <div>Submitted: {formatTimestamp(performer?.submittedAt)}</div>
                    {performer?.replacedAt ? <div>Replaced: {formatTimestamp(performer.replacedAt)}</div> : null}
                    {performer?.cancelledAt ? <div>Cancelled: {formatTimestamp(performer.cancelledAt)}</div> : null}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-sm text-slate-400">No performers assigned yet.</div>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-black/20 p-3">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Warnings</div>
        <div className="mt-3 grid gap-2">
          {displayWarnings.length ? (
            displayWarnings.map((warning, index) => (
              <div key={`${warning}-${index}`} className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                {warning}
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100">
              No outstanding lineup warnings.
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-black/20 p-3">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Submitted Artwork</div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {artworkEntries.length ? (
            artworkEntries.map((performer, index) => (
              <div key={performer?.id || `artwork-${index}`} className="overflow-hidden rounded-xl bg-white/5 ring-1 ring-white/10">
                <img
                  src={performer.artworkThumbnailUrl}
                  alt={performer?.displayName || 'Submitted artwork'}
                  className="h-40 w-full bg-slate-950/80 object-contain p-2"
                />
                <div className="px-3 py-2 text-xs text-slate-300">
                  {performer?.displayName || `Performer ${index + 1}`}
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-dashed border-white/10 px-3 py-4 text-sm text-slate-400 sm:col-span-2">
              No submitted artwork previews yet.
            </div>
          )}
        </div>
      </div>

      {historyPerformers.length ? (
        <div className="mt-4 text-xs text-slate-500">
          {historyPerformers.length} historical performer row{historyPerformers.length === 1 ? '' : 's'} retained for audit.
        </div>
      ) : null}

      {canCreateCampaign ? (
        <div className="mt-4 rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-3">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-indigo-500/20 p-2 text-indigo-100">
              <Megaphone className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white">Create Campaign</div>
              <div className="mt-1 text-sm text-indigo-100">
                Published gigs can move straight into Campaign Creator in Creative Studio. The gig and performer details will prefill the campaign brief automatically.
              </div>
              <button
                type="button"
                onClick={onCreateCampaign}
                disabled={busy}
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
              >
                <Megaphone className="h-4 w-4" />
                Create a Campaign
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20 disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          Save Details
        </button>
        <button
          type="button"
          onClick={onRequestChanges}
          disabled={busy || !canRequestChanges}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20 disabled:opacity-60"
        >
          <Upload className="h-4 w-4" />
          Request Changes
        </button>
        <button
          type="button"
          onClick={onPublish}
          disabled={busy || !canPublish}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600/90 px-3 py-2 text-sm text-white hover:bg-emerald-600 disabled:opacity-60"
        >
          <Upload className="h-4 w-4" />
          Publish
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy || !canCancel}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-600/90 px-3 py-2 text-sm text-white hover:bg-rose-600 disabled:opacity-60"
        >
          <Ban className="h-4 w-4" />
          Cancel Gig
        </button>
      </div>
    </div>
  );
}
